import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useAuthenticatedUser, useFeature, useLocalize, useMixpanel } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';

import useTalentReviewPath from '../../../../../talent-reviews/v2/hooks/useTalentReviewPath';
import { formatTalentReviewDeadline } from '../../../../../talent-reviews/v2/talentReviewDate';
import DateService from '../../../../kakapo-ui-services/services/common/DateService';
import InboxTaskItem from '../components/InboxTaskItem';

export default function InboxMomentStartTask({
	task,
	onAction,
	refresh,
}: {
	task: graphql.InboxTask;
	onAction: () => void;
	refresh: () => void;
}) {
	const mixpanel = useMixpanel();
	const history = useHistory();
	const talentReviewPath = useTalentReviewPath();
	const hasTalentReviewsV2 = useFeature(graphql.FeatureName.TalentReviewsV2);
	const { t } = useTranslation();
	const authenticatedUser = useAuthenticatedUser();
	const { localize } = useLocalize();

	const [completeMomentInstance] = graphql.useCompleteMomentInstanceTaskMutation();

	const { moment: momentInstance } = task;

	const { id: momentID, endDate, momentTemplate } = momentInstance!;
	const { type, title } = momentTemplate;
	const momentName = localize(title).text;
	const groupName = momentTemplate.talentReview?.group?.name ?? momentName;
	const isTalentReview = type === graphql.MomentType.TalentReview;

	const onClick = async () => {
		onAction();
		switch (type) {
			case graphql.MomentType.Goals:
				mixpanel?.track('inbox.tasks.moments.goals.start');
				history.push(`/goals/new?moment_id=${momentID}`);
				break;
			case graphql.MomentType.Feedback:
				mixpanel?.track('inbox.tasks.moments.feedback.start');
				history.push(`/users/${authenticatedUser.id}/feedback/request-from-moment/${momentID}`);
				break;
			case graphql.MomentType.Evaluation:
				mixpanel?.track('inbox.tasks.moments.evaluation.start');
				history.push(
					`/users/${authenticatedUser.id}/conversations/request-from-moment/${momentID}`,
				);
				break;
			case graphql.MomentType.Message:
				mixpanel?.track('inbox.tasks.moments.message.start');
				await completeMomentInstance({
					variables: {
						input: {
							id: momentID,
						},
					},
				});
				refresh();
				break;
			case graphql.MomentType.TalentReview: {
				mixpanel?.track('inbox.tasks.moments.talentReview.start');
				// The v2 pages redirect to v1 without the feature, and v1 has no
				// group/template deep link, so send users to the v1 index instead.
				history.push(
					hasTalentReviewsV2
						? talentReviewPath({
								groupID: momentTemplate.talentReview?.group?.id,
								templateID: momentTemplate.talentReview?.template?.id,
								momentID,
							})
						: '/talent-reviews',
				);
				break;
			}
			default:
				break;
		}
	};

	let key: string | undefined;
	let buttonTitle = t('inbox.tasks.button.start');

	switch (type) {
		case graphql.MomentType.Goals:
			key = 'goal';
			break;
		case graphql.MomentType.Feedback:
			key = 'feedback';
			break;
		case graphql.MomentType.Evaluation:
			key = 'evaluation';
			break;
		case graphql.MomentType.Message:
			key = 'message';
			buttonTitle = t('inbox.tasks.button.complete');
			break;
		case graphql.MomentType.TalentReview:
			key = 'talentReview';
			break;
		default:
			break;
	}

	return (
		<InboxTaskItem
			task={task}
			title={
				isTalentReview
					? t('inbox.tasks.talentReviewStartMomentSelf.title', { groupName })
					: t(`inbox.tasks.${key}StartMomentSelf.title`, { momentName })
			}
			subtitle={
				isTalentReview
					? t('inbox.tasks.talentReviewStartMomentSelf.subtitle', {
							momentName,
							createdAt: DateService.daysAgo(new Date(task.createdAt), new Date(), t),
							deadline: formatTalentReviewDeadline(endDate),
						})
					: undefined
			}
			modalTitle={
				isTalentReview
					? t('inbox.tasks.talentReviewStartMomentSelf.modal.title', { groupName })
					: undefined
			}
			modalButtonTitle={
				isTalentReview ? t('inbox.tasks.talentReviewStartMomentSelf.modal.button') : undefined
			}
			date={task.createdAt}
			instruction={momentTemplate.description ?? undefined}
			buttonTitle={buttonTitle}
			onClick={onClick}
			refresh={refresh}
		/>
	);
}
